import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, MessageSquare, ClipboardList, Eye, EyeOff, X, Briefcase } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Brand, UserMenu } from "@/components/Shell";
import { CafeChat } from "@/components/cafe/CafeChat";
import { Board } from "@/components/cafe/Board";
import { FeaturedPartner } from "@/components/cafe/FeaturedPartner";
import { BuyMeACoffee } from "@/components/cafe/BuyMeACoffee";
import { JobBoard } from "@/components/cafe/JobBoard";
import { useCafeSync } from "@/components/cafe/CafeOthers";
import { toast } from "sonner";

const CafeScene = lazy(() => import("@/components/cafe/CafeScene"));
const ROOMS = [["generale", "Sala generale"], ["lavoro", "Lavoro"], ["infermieri", "Infermieri"], ["medici", "Medici"], ["oss", "OSS"]];

function usePresence(room, visible) {
  const { user } = useAuth();
  const [who, setWho] = useState({ count: 0, people: [] });
  useEffect(() => {
    const tick = async () => {
      if (user) await api.post("/cafe/presence", { room, visible }).catch(() => {});
      api.get("/cafe/presence").then(({ data }) => setWho(data)).catch(() => {});
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [user, room, visible]);
  return who;
}

export default function CafePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [room, setRoom] = useState("generale");
  const [tab, setTab] = useState("chat");
  const [visible, setVisible] = useState(true);
  const [panel, setPanel] = useState(false);
  const who = usePresence(room, visible);
  const others = useCafeSync(user, visible);
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    api.get("/jobs", { params: { size: 3 } }).then(({ data }) => setJobs(data)).catch(() => {});
  }, []);
  const low = typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;
  return (
    <div className="relative h-screen w-full overflow-hidden bg-emerald-50" data-testid="cafe-page">
      <Suspense fallback={null}><CafeScene onOpenBoard={() => { setTab("board"); setPanel(true); }} onOpenPartners={() => navigate("/partner")} onOpenJobs={() => { setTab("jobs"); setPanel(true); }} jobs={jobs} low={low} others={others} onSeatTaken={() => toast.info("Questa sedia è già occupata o sei già seduto qui")} me={user ? { gender: user.avatar_gender || "uomo", name: (user.name || "").split(" ")[0] } : null} /></Suspense>
      {user && !panel && <p data-testid="cafe-avatar-hint" className="glass pointer-events-none absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 rounded-full px-4 py-2 text-xs font-semibold text-slate-700 sm:block">Clicca sul pavimento o usa W A S D per muoverti · clicca una sedia per sederti</p>}
      <header className="absolute inset-x-0 top-0 z-10 flex items-center justify-between px-4 py-3 sm:px-6">
        <div className="flex items-center gap-3"><Link to="/" data-testid="cafe-back-btn" className="glass grid h-10 w-10 place-items-center rounded-full text-slate-700"><ArrowLeft className="h-4 w-4" /></Link><span className="hidden sm:block"><Brand /></span></div>
        <UserMenu compact />
      </header>
      <BuyMeACoffee />
      {!panel && <FeaturedPartner />}
      {!panel && <button data-testid="cafe-panel-toggle" onClick={() => setPanel(true)} aria-label="Apri chat e bacheca" title="Apri chat e bacheca" className="absolute bottom-28 right-4 z-20 grid h-14 w-14 place-items-center rounded-full bg-emerald-600 text-white shadow-lg transition-transform hover:scale-105 sm:right-6"><MessageSquare className="h-6 w-6" />{who.count > 0 && <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold">{who.count}</span>}</button>}
      <aside className={`glass absolute bottom-3 right-3 top-20 z-10 w-[calc(100%-1.5rem)] max-w-md flex-col rounded-3xl p-4 sm:bottom-6 sm:right-6 ${panel ? "flex" : "hidden"}`} data-testid="cafe-panel">
        <div className="mb-3 flex items-center justify-between">
          <p className="font-display text-lg font-extrabold text-slate-900">Selvix Café</p>
          <p className="flex items-center gap-1 text-xs text-slate-600" data-testid="cafe-presence-count"><Users className="h-3.5 w-3.5" />{who.count} presenti<button data-testid="cafe-panel-close" onClick={() => setPanel(false)} aria-label="Riduci a icona" className="ml-2 grid h-7 w-7 place-items-center rounded-full bg-white/80 text-slate-600 hover:bg-white"><X className="h-4 w-4" /></button></p>
        </div>
        <div className="mb-3 flex gap-1 rounded-full bg-white/70 p-1">
          {[["chat", "Chat", MessageSquare], ["board", "Bacheca", ClipboardList], ["jobs", "Lavoro", Briefcase], ["people", "Presenti", Users]].map(([k, l, I]) => (
            <button key={k} data-testid={`cafe-tab-${k}`} onClick={() => setTab(k)} className={`flex flex-1 items-center justify-center gap-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${tab === k ? "bg-emerald-600 text-white" : "text-slate-600"}`}><I className="h-3.5 w-3.5" />{l}</button>
          ))}
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {tab === "chat" && (
            <div className="flex h-full flex-col">
              <div className="mb-2 flex flex-wrap gap-1">{ROOMS.map(([k, l]) => <button key={k} data-testid={`cafe-room-${k}`} onClick={() => setRoom(k)} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${room === k ? "bg-emerald-100 text-emerald-800" : "bg-white/70 text-slate-600"}`}>{l}</button>)}</div>
              <div className="min-h-0 flex-1"><CafeChat room={room} /></div>
            </div>
          )}
          {tab === "board" && <Board />}
          {tab === "jobs" && <JobBoard />}
          {tab === "people" && (
            <div className="space-y-2" data-testid="cafe-people">
              {user && <button data-testid="cafe-visibility-toggle" onClick={() => setVisible(!visible)} className="flex items-center gap-1 text-xs font-semibold text-emerald-700">{visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}{visible ? "Sei visibile agli altri" : "Sei nascosto"}</button>}
              {!user && <p className="text-xs text-slate-500">Accedi per vedere chi è presente.</p>}
              {who.people.map((p) => <p key={p.user_id} className="rounded-xl bg-white/80 px-3 py-2 text-sm" data-testid={`cafe-person-${p.user_id}`}><b>{p.name}</b> <span className="text-xs text-slate-500">· {ROOMS.find(([k]) => k === p.room)?.[1]}</span></p>)}
            </div>
          )}
        </div>
        <p className="mt-2 text-[10px] text-slate-500">Spazio sociale moderato: non condividere dati sanitari o personali. Doctor Selvix è un assistente AI, non un medico.</p>
      </aside>
    </div>
  );
}
